import type { TFunction } from 'i18next'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import type { TransKey } from '@/i18n'
import { OfficeRoutePath, RoutePath } from '@/RoutePath'

const SITE_SUFFIX = ' | ShipTanbul'

type PageTitleEntry = { pattern: string; title: string } | { pattern: string; titleKey: TransKey }

/** Map of path patterns to page titles (without suffix). Exact paths first, then prefixes for dynamic routes. */
const PAGE_TITLES: PageTitleEntry[] = [
  // Auth
  { pattern: RoutePath.Login, title: 'התחברות לאזור האישי' },
  { pattern: RoutePath.Welcome, title: 'התחברות לאזור האישי' },
  { pattern: RoutePath.ForgotPassword, title: 'שחזור סיסמה' },
  // Client area
  { pattern: RoutePath.ClientAddresses, title: 'הכתובות שלי' },
  { pattern: RoutePath.ClientPkgUnite, title: 'איחוד חבילות' },
  { pattern: RoutePath.ClientPkg, title: 'החבילות שלי' },
  { pattern: RoutePath.ClientProfileAffiliate, title: 'שיווק שותפים' },
  { pattern: RoutePath.ClientProfileAddresses, title: 'הפרופיל שלי' },
  { pattern: RoutePath.ClientProfilePersonal, title: 'הפרופיל שלי' },
  { pattern: RoutePath.ClientProfile, title: 'הפרופיל שלי' },
  // Dynamic client routes (prefix match)
  { pattern: RoutePath.ClientPkgReturn, title: 'החזרת חבילה' },
  { pattern: RoutePath.ReceiptUpload, title: 'העלאת קבלה' },
  { pattern: '/payment', title: 'תשלום' },
  // Office (English)
  { pattern: OfficeRoutePath.Ship, title: 'Ship Packages' },
  { pattern: OfficeRoutePath.EditAndUpdate, title: 'Find & Update' },
  { pattern: OfficeRoutePath.UserPage, title: 'User Page' },
  { pattern: OfficeRoutePath.PkgRequests, title: 'Package Requests' },
  { pattern: OfficeRoutePath.CheckForm, title: 'Packages Check List' },
  { pattern: OfficeRoutePath.Stats, title: 'Stats' },
  { pattern: OfficeRoutePath.PopularBrandsItems, title: 'Popular Brands & Items' },
  { pattern: OfficeRoutePath.Affiliate, title: 'Affiliate' },
  { pattern: OfficeRoutePath.Coupon, title: 'Coupons' },
  { pattern: OfficeRoutePath.BarcodeGunShip, title: 'Barcode Gun Ship' },
  { pattern: OfficeRoutePath.Shelfs, title: 'Browse Shelves' },
  { pattern: OfficeRoutePath.PkgLocation, title: 'Location' },
  { pattern: OfficeRoutePath.WarehouseArrivals, title: 'Warehouse Arrivals' },
  { pattern: OfficeRoutePath.PkgLookup, title: 'Package Lookup' },
  { pattern: OfficeRoutePath.PkgLookupsList, title: 'Lookups List' },
  { pattern: OfficeRoutePath.PkgCases, title: 'Missing Packages' },
  { pattern: OfficeRoutePath.DelayedPkgs, title: 'Delayed Packages' },
  { pattern: OfficeRoutePath.PickupOrders, title: 'Pickup Orders' },
  { pattern: OfficeRoutePath.ScanPickupOrders, title: 'Scan Orders' },
  { pattern: OfficeRoutePath.ServiceReqs, title: 'Service Requests' },
  { pattern: OfficeRoutePath.Sticker, title: 'Sticker' },
  { pattern: OfficeRoutePath.AdminSettings, title: 'Admin Settings' },
  { pattern: OfficeRoutePath.AdminData, title: 'Admin Data' },
  { pattern: OfficeRoutePath.AuditLog, title: 'Audit Log' },
  { pattern: OfficeRoutePath.FinanceMicro, titleKey: 'finance.pageTitles.micro' },
  { pattern: OfficeRoutePath.FinanceMacro, titleKey: 'finance.pageTitles.macro' },
  { pattern: OfficeRoutePath.FinanceSettings, titleKey: 'finance.pageTitles.settings' },
  { pattern: OfficeRoutePath.ReceiptTextractTest, title: 'Receipt extraction test' },
  { pattern: OfficeRoutePath.AffiliateManager, title: 'Affiliate Manager' },
  { pattern: OfficeRoutePath.MarketingDashboard, titleKey: 'marketing.dashboard.pageTitle' },
  { pattern: OfficeRoutePath.MarketingCampaigns, title: 'Campaigns' },
  { pattern: OfficeRoutePath.MarketingInvoices, title: 'Marketing Invoices' },
  { pattern: OfficeRoutePath.MarketingAffiliatePackages, title: 'Affiliate Packages' },
  { pattern: OfficeRoutePath.MarketingEmails, title: 'Marketing Emails' },
  { pattern: OfficeRoutePath.AddNewPkg, title: 'Add New Package' },
]

const DEFAULT_TITLE = 'אזור אישי' + SITE_SUFFIX

function resolveTitle(entry: PageTitleEntry, t: TFunction): string {
  return ('titleKey' in entry ? t(entry.titleKey) : entry.title) + SITE_SUFFIX
}

function getStaticTitleForPath(pathname: string, t: TFunction): string {
  const exact = PAGE_TITLES.find(({ pattern }) => pathname === pattern || pathname === pattern + '/')
  if (exact) return resolveTitle(exact, t)

  const prefix = PAGE_TITLES.find(({ pattern }) => pathname.startsWith(pattern + '/'))
  if (prefix) return resolveTitle(prefix, t)

  if (pathname === '/' || pathname === '') return 'התחברות לאזור האישי' + SITE_SUFFIX

  return DEFAULT_TITLE
}

/**
 * Sets document.title based on the current route. Use once in the app root (e.g. Layout).
 */
export function usePageTitle(): void {
  const { pathname } = useLocation()
  const { t } = useTranslation()

  useEffect(() => {
    document.title = getStaticTitleForPath(pathname, t)
  }, [pathname, t])
}
